import { KeyboardEvent, useRef, useState, InputHTMLAttributes } from 'react';

import { KEYBOARD_CODES } from '../../../../constants';
import type { InputWrapperProps } from '../types';
import { excludeWrapperProps } from '../utils';

export type TagInputProps = Omit<InputWrapperProps, 'value' | 'onChange'> & {
  value?: string[];
  capitalize?: boolean;
  isLarge?: boolean;
  isValid?: (value: string) => boolean;
  onChanged?: (value: string[]) => void;
  onChanging?: (value: string) => void;
  maxTagCount?: number;
};

type TagInputReturnType = {
  inputRef: React.RefObject<HTMLInputElement>;
  inputValue: string;
  showTextInput: boolean;
  showPlaceholder: boolean;
  inputProps: InputHTMLAttributes<HTMLInputElement>;
  handleTagsChange: (newTags: string[]) => void;
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  addTag: () => void;
  removeTag: (tagToRemove: string) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: () => void;
};

/**
 * Hook for handling TagInput logic and state
 */
export const useTagInputProps = (props: TagInputProps): TagInputReturnType => {
  const { value = [], onChanged, maxTagCount = 99, ...restProps } = props;

  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter out wrapper props
  const inputProps = excludeWrapperProps(restProps);

  // Custom controlled input handling for array values
  const handleTagsChange = (newTags: string[]) => {
    if (onChanged) {
      onChanged(newTags);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case KEYBOARD_CODES.ENTER:
      case KEYBOARD_CODES.NUMPAD_ENTER:
      case KEYBOARD_CODES.COMMA:
      case KEYBOARD_CODES.SPACE:
        e.preventDefault();
        addTag();
        break;
      case KEYBOARD_CODES.BACKSPACE:
        if (inputValue === '' && value.length > 0) {
          handleTagsChange(value.slice(0, -1));
        }
    }
  };

  const addTag = () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) {
      setInputValue('');
      return;
    }

    if (!props.isValid?.(trimmedValue)) {
      return;
    }

    // Don't add duplicate tags
    if (!value.includes(trimmedValue.toLowerCase())) {
      const newTags = value.concat(trimmedValue.toLowerCase());
      handleTagsChange(newTags);
    }

    setInputValue('');
  };

  const removeTag = (tagToRemove: string) => {
    const newTags = value.filter((tag: string) => tag !== tagToRemove);
    handleTagsChange(newTags);
    inputRef.current?.focus();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    props.onChanging?.(newValue);
  };

  const handleBlur = () => {
    addTag();
    props.onBlur?.();
  };

  const showTextInput = value.length < maxTagCount;
  const showPlaceholder = value.length === 0;

  return {
    inputRef,
    inputValue,
    showTextInput,
    showPlaceholder,
    inputProps,
    handleTagsChange,
    handleKeyDown,
    addTag,
    removeTag,
    handleChange,
    handleBlur,
  };
};
